import React, { useState, useRef, useEffect } from "react";
import Dashboardsidebar from "../../layouts/Dashboardsidebar";
import * as CONSTANTS from "../dashboard/constants/DashboardConstants";
import { useNavigate, useLocation } from "react-router-dom";
import SuccessModal from "../../layouts/SuccessModal";
import PropTypes from "prop-types";
import { injectModels } from "../../redux/injectModels";
import { debounce } from "lodash";
import ReactDOM from "react-dom";
import Footer from "../../layouts/Footer";

const SendPage = ({ auth, loader }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [files, setFiles] = useState([]);
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([]);
  const [agreementName, setAgreementName] = useState("");
  const [success, setSuccess] = useState(false);
  const [mode, setMode] = useState("create");
  const [agreementId, setAgreementId] = useState(null);

  const [recipients, setRecipients] = useState([{ role: "", email: "" }]);

  const [activeInputIndex, setActiveInputIndex] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [dropdownPos, setDropdownPos] = useState(null);
  const [redirectTarget, setRedirectTarget] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const dropdownRef = useRef(null);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const recipientListRef = useRef(null);
  const [originalData, setOriginalData] = useState(null);

  const [cancelSuccess, setCancelSuccess] = useState(false);
  const cancelRedirectRef = useRef(null);

  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + 30);
  const todayStr = today.toISOString().split("T")[0];
  const maxDateStr = maxDate.toISOString().split("T")[0];
  const [dueDate, setDueDate] = useState(maxDateStr);

  const allowedExtensions = [
    "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "jpg", "jpeg", "png", "webp", "eml", "msg", "zip",
  ];

  const allowedFormats = [
    // PDFs
    "application/pdf",

    // Word
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",

    // Excel
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

    // PowerPoint
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",

    // Images
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/webp",

    // Emails
    "message/rfc822",              // .eml
    "application/vnd.ms-outlook",  // .msg

    // ZIP
    "application/zip",
    "application/x-zip-compressed",
  ];

  // Dirty check
  const isEditing = mode === "edit" && originalData !== null;

  const isDirty =
    isEditing &&
    (agreementName !== originalData.agreementName ||
      dueDate !== originalData.dueDate ||
      JSON.stringify(recipients.map((r) => ({ email: r.email, role: r.role }))) !==
      JSON.stringify(originalData.recipients) ||
      JSON.stringify(files.map((f) => f.name)) !== JSON.stringify(originalData.files));

  // Load existing data if editing/viewing
  useEffect(() => {
    if (location.state?.mode) {
      setMode(location.state.mode);
    } else if (location.state?.agreementData?.status === "in_progress") {
      setMode("view");
    }

    if (location.state?.agreementData) {
      const existing = location.state.agreementData;

      setAgreementId(existing.id || null);
      setAgreementName(existing.agreement_name || existing.file_name || "");

      if (existing.due_date) {
        const raw = existing.due_date;
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          setDueDate(raw);
        } else if (raw.includes("T")) {
          setDueDate(raw.split("T")[0]);
        } else {
          setDueDate(maxDateStr);
        }
      } else {
        setDueDate(maxDateStr);
      }

      if (Array.isArray(existing.recipients)) {
        const formatted = existing.recipients.map((r) => ({
          id: r.id,
          name: r.name || (r.email && r.email.split("@")[0]) || "",
          email: r.email || "",
          role: r.role
            ? r.role.charAt(0).toUpperCase() + r.role.slice(1).toLowerCase()
            : "",
          status: r.status || "pending",
        }));
        setRecipients(formatted);
      }

      const loadedFiles = [];
      if (Array.isArray(existing.documents) && existing.documents.length > 0) {
        const doc = existing.documents[0];
        const ext = doc.name.split(".").pop().toLowerCase();
        const mapExt = {
          pdf: "application/pdf",
          docx:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          doc: "application/msword",
        };
        loadedFiles.push({
          existing: true,
          id: doc.id,
          name: doc.name,
          size: doc.size || null,
          type: mapExt[ext] || null,
          path: doc.path || null,
          file_url: null,
          binary: null,
        });
      }
      setFiles(loadedFiles);

      setOriginalData({
        agreementName: existing.agreement_name || existing.file_name || "",
        dueDate: existing.due_date ? existing.due_date.split("T")[0] : maxDateStr,
        recipients: Array.isArray(existing.recipients)
          ? existing.recipients.map((r) => ({
            email: r.email,
            role: r.role
              ? r.role.charAt(0).toUpperCase() + r.role.slice(1).toLowerCase()
              : "",
          }))
          : [],
        files: Array.isArray(existing.documents) ? existing.documents.map((d) => d.name) : [],
      });
    }
  }, [location.state, maxDateStr]);

  // File handling
  const validateAndAddFiles = (uploadedFiles) => {
    const file = uploadedFiles[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();

    const isMimeAllowed = allowedFormats.includes(file.type);
    const isExtAllowed = allowedExtensions.includes(ext);

    if (!isMimeAllowed && !isExtAllowed) {
      alert(
        `${file.name}: Unsupported format.\n\nAllowed: Office files, PDFs, Images, Emails (.eml/.msg), ZIP`
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert(`${file.name}: Max file size is 10MB`);
      return;
    }

    const newFile = {
      existing: false,
      id: null,
      name: file.name,
      size: file.size,
      type: file.type || ext,
      path: null,
      file_url: null,
      binary: file,
    };

    setFiles([newFile]);
    setAgreementName(file.name);
  };

  const handleFileChange = (e) => validateAndAddFiles(Array.from(e.target.files));
  const handleDrop = (e) => {
    e.preventDefault();
    validateAndAddFiles(Array.from(e.dataTransfer.files));
  };
  const handleDragOver = (e) => e.preventDefault();

  const handleDeleteFile = (index) => {
    const file = files[index];
    if (file && file.existing && file.id) {
      setDeletedDocumentIds((prev) => [...prev, file.id]);
    }
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    if (updated.length === 0) setAgreementName("");
  };

  // Recipients
  const handleRecipientChange = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const handleDeleteRecipient = (index) => {
    if (mode === "view" || index === 0) return;
    setRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddRecipient = () => {
    if (mode === "view") return;
    setRecipients((prev) => [...prev, { id: null, role: "", email: "" }]);

    setTimeout(() => {
      if (!recipientListRef.current) return;
      const rows = recipientListRef.current.children;
      if (rows[rows.length - 1]) {
        rows[rows.length - 1].scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);
  };

  const fetchSuggestions = useRef(
    debounce(async (query) => {
      console.log(" Searching for:", query);

      if (!query || query.length < 2) {
        setSuggestions([]);
        return;
      }

      const res = await auth.getUserInfoList(query);
      console.log(" API Response:", res);

      if (!res?.success || !Array.isArray(res.data)) {
        setSuggestions([]);
        return;
      }

      const filtered = res.data.filter((u) => {
        const t = query.toLowerCase();
        return (
          u.firstName.toLowerCase().includes(t) ||
          u.lastName.toLowerCase().includes(t) ||
          u.email.toLowerCase().includes(t)
        );
      });

      setSuggestions(filtered);
    }, 300)
  ).current;

  const updateDropdownPosition = (inputEl) => {
    if (!inputEl) return;
    const rect = inputEl.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    setDropdownPos({
      top: rect.bottom + scrollTop,
      left: rect.left + scrollLeft,
      width: rect.width,
    });
  };

  const handleInputFocus = (e, index) => {
    setActiveInputIndex(index);
    updateDropdownPosition(e.target);
    if (e.target.value && e.target.value.length >= 2) {
      fetchSuggestions(e.target.value);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (index, email) => {
    if (index == null) return;
    const updated = [...recipients];
    updated[index] = { ...updated[index], email: email || "" };
    setRecipients(updated);
    setSuggestions([]);
    setActiveInputIndex(null);
  };

  useEffect(() => {
    const handler = (e) => {
      const insideList = recipientListRef.current && recipientListRef.current.contains(e.target);
      const insidePortal = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideList && !insidePortal) {
        setActiveInputIndex(null);
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, []);

  // Documents builder
  const buildDocuments = () =>
    files.map((f) => ({
      id: f.id || null,
      name: f.name,
      type: f.type,
      size: f.size,
      path: f.path || null,
      file_url: f.file_url || null,
      binary: f.existing ? null : f.binary,
    }));

  // Validation
  const validateForm = () => {
    const fileName = agreementName || (files[0] && files[0].name);
    if (!fileName) return "Please upload a document.";

    for (const r of recipients) {
      if (!r.role || r.role.trim() === "") return "Please select a Recipient role for all recipients.";
      if (!r.email || r.email.trim() === "") return "Please enter an email for all recipients.";
      const email = r.email.trim();
      const domainRegex = /^[^\s@]+@[^\s@]+\.com$/i;
      if (!domainRegex.test(email)) {
        return `Invalid email domain: '${email}'. Only .com emails are allowed.`;
      }
    }
    return null;
  };

  // Save
  const handleSaveClick = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const fileName = agreementName || (files[0] && files[0].name);

    const validRecipients = recipients.map((r) => ({
      id: r.id || null,
      name: r.email.split("@")[0],
      email: r.email.trim().toLowerCase(),
      role: r.role,
      status: "pending",
    }));

    const isEditing = Boolean(agreementId);
    const hasNewFile = files.some((f) => !f.existing);

    const payload = {
      agreement_id: agreementId || null,
      agreement_name: fileName,
      status: "draft",
      due_date: dueDate,
      recipients: validRecipients,
    };

    if (!isEditing) {
      payload.documents = buildDocuments();
      payload.deleted_documents = [];
    } else if (isEditing && !hasNewFile) {
      payload.documents = buildDocuments();
      payload.deleted_documents = [];
    } else if (isEditing && hasNewFile) {
      payload.documents = buildDocuments();
      payload.deleted_documents = deletedDocumentIds;
    }

    loader.setLoading(true);
    const res = await auth.createNewSaveAgreement(payload);
    loader.setLoading(false);

    if (res && res.success) {
      setRedirectTarget("draft");
      setSuccessMessage("Draft saved successfully.");
      setSuccess(true);
    } else {
      setErrorMessage(res?.message || "Failed to save draft.");
    }
  };

  // Send
  const handleSendClick = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const fileName = agreementName || (files[0] && files[0].name);

    const validRecipients = recipients.map((r) => ({
      id: r.id || null,
      name: r.email.split("@")[0],
      email: r.email.trim().toLowerCase(),
      role: r.role,
      status: "pending",
    }));

    const isEditing = Boolean(agreementId);
    const hasNewFile = files.some((f) => !f.existing);

    const payload = {
      agreement_id: agreementId || null,
      agreement_name: fileName,
      status: "in_progress",
      due_date: dueDate,
      recipients: validRecipients,
    };

    if (!isEditing) {
      payload.documents = buildDocuments();
      payload.deleted_documents = [];
    } else if (isEditing && !hasNewFile) {
      payload.documents = buildDocuments();
      payload.deleted_documents = [];
    } else if (isEditing && hasNewFile) {
      payload.documents = buildDocuments();
      payload.deleted_documents = deletedDocumentIds;
    }

    loader.setLoading(true);
    const res = await auth.sendAgreementAction(payload);
    loader.setLoading(false);

    if (res && res.success) {
      setRedirectTarget("in_progress");
      setSuccessMessage("document sent successfully.");
      setSuccess(true);
    } else {
      setErrorMessage(res?.message || "Failed to send document .");
    }
  };

  const handleSuccessClose = () => {
    setSuccess(false);

    if (redirectTarget) {
      navigate("/manage", {
        state: { activeTab: redirectTarget },
      });
    }
  };

  // Cancel
  const handleCancelClick = async () => {
    if (mode === "view") return navigate("/manage", { state: { activeTab: "in_progress" } });
    if (mode === "edit") return setShowCancelConfirm(true);
    if (!agreementId) return navigate("/manage", { state: { activeTab: "draft" } });
  };

  const handleConfirmCancel = async () => {
    loader.setLoading(true);

    const res = await auth.cancelAgreementUploadData(agreementId);

    loader.setLoading(false);
    setShowCancelConfirm(false);

    if (res && res.success) {
      setCancelSuccess(true);

      // auto close after 1 minute (optional)
      cancelRedirectRef.current = setTimeout(() => {
        handleCancelSuccessClose();
      }, 60000);
    } else {
      alert(res?.message || "Failed to cancel.");
    }
  };

  const handleCancelSuccessClose = () => {
    clearTimeout(cancelRedirectRef.current);

    navigate("/manage", {
      state: {
        activeTab: "cancelled",
      },
    });
  };


  // Render
  return (
    <Dashboardsidebar>
      <div className={`sendpage ${mode === "view" ? "view-mode" : ""}`}>
        <h3>{CONSTANTS.GET_DOCUMENT_SIGNED}</h3>
        <p>{CONSTANTS.GET_DOCUMENT_SIGNED_TEXT}</p>

        <div className="sendpage-content">
          {/* Upload Section */}
          <div className="col-sm-6">
            <div className="upload-section" onDrop={handleDrop} onDragOver={handleDragOver}>
              <h4 className="section-title">Add documents</h4>

              <div className="upload-box">
                <div className="upload-icon">
                  <i className="fa fa-cloud-upload" />
                </div>
                <p className="drag-text">
                  {CONSTANTS.DRAG_AND_DROP_FILE}{" "}
                  {mode !== "view" && (
                    <button className="btn btn-sm ms-2"
                      style={{
                        backgroundColor: "#1c2c4c",
                        color: "#ffffff",
                        border: "none",
                      }}
                      onClick={() => fileInputRef.current.click()}>
                      {CONSTANTS.BROWSE}
                    </button>
                  )}
                </p>
                <small style={{ position: "relative", bottom: "1rem", right: "3rem" }}>{CONSTANTS.SUPPORTED_FORMMATS}</small>
              </div>

              {mode !== "view" && (
                <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
              )}

              {files.map((file, i) => (
                <div className="file-card" key={i}>
                  <div className="file-left">
                    <i className={`fa ${file.name && file.name.endsWith(".pdf") ? "fa-file-pdf" : "fa-file-word"} file-icon`} />
                    <span>
                      {file.name}
                      {file.existing && " (Existing File)"}
                    </span>
                  </div>

                  {mode !== "view" && (
                    <button className="file-delete" onClick={() => handleDeleteFile(i)}>
                      <i className="fa fa-trash" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Agreement Info */}
          <div className="col-sm-6">
            <div className="agreement-section">
              <div className="form-group">
                <label>Document Name</label>
                <input
                  type="text"
                  value={agreementName}
                  readOnly={mode === "view"}
                  onChange={(e) => setAgreementName(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>{CONSTANTS.DUE_DATE}</label>
                <input
                  type="date"
                  className="form-control"
                  min={todayStr}
                  max={maxDateStr}
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={mode === "view"}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Recipient Section */}
        <div className="recipient-section">
          <div className="recipient-list" ref={recipientListRef}>
            {recipients.map((rec, i) => (
              <div className="recipient-row" key={rec.id || i}>
                <div className="recipient-number">{i + 1}</div>

                <div className="recipient-fields">
                  <div className="col-sm-2">
                    <label>Recipient</label>

                    <select
                      className="form-control"
                      value={rec.role}
                      onChange={(e) => handleRecipientChange(i, "role", e.target.value)}
                      disabled={mode === "view"}
                    >
                      <option value=""></option>
                      <option value="Author">Author</option>
                      <option value="Reviewer">Reviewer</option>
                      <option value="Approver">Approver</option>
                    </select>
                  </div>

                  <div className="field-group" style={{ position: "relative" }}>
                    <label>Email</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter name or email"
                      value={rec.email}
                      disabled={mode === "view"}
                      onFocus={(e) => handleInputFocus(e, i)}
                      onChange={(e) => {
                        handleRecipientChange(i, "email", e.target.value);
                        setActiveInputIndex(i);
                        fetchSuggestions(e.target.value);
                        updateDropdownPosition(e.target);
                      }}
                    />

                    {/* Suggestion dropdown via portal */}
                    {activeInputIndex === i && suggestions.length > 0 && dropdownPos &&
                      ReactDOM.createPortal(
                        <div
                          ref={dropdownRef}
                          className="suggestion-box-portal"
                          style={{
                            position: "absolute",
                            top: dropdownPos.top,
                            left: dropdownPos.left,
                            width: dropdownPos.width,
                            background: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            zIndex: 1000000000,
                            maxHeight: 260,
                            overflowY: "auto",
                          }}
                        >
                          {suggestions.map((s, idx) => (
                            <div
                              key={idx}
                              className="suggestion-item"
                              style={{
                                padding: 10,
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                              }}
                              onClick={() => handleSelectSuggestion(i, s.email)}
                            >
                              <div>
                                {s.firstName} {s.lastName}
                              </div>
                              <div style={{ color: "#777", fontSize: 12 }}>
                                {s.email}
                              </div>
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                  </div>
                </div>

                {mode !== "view" && (
                  <button className="delete-btn" disabled={i === 0} onClick={() => handleDeleteRecipient(i)}>
                    <i className="fa fa-trash" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {!mode.includes("view") && (
            <div className="recipient-footer">
              <button className="btn-add" onClick={handleAddRecipient}>
                + Add Recipient
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="send-actions">
          {mode !== "view" && (
            <>
              <button
                className="btn-save"
                disabled={isEditing && !isDirty}
                style={isEditing && !isDirty ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                onClick={handleSaveClick}
              >
                <i className="fa fa-floppy-disk" /> Save
              </button>

              <button className="btn-send" onClick={handleSendClick}>
                <i className="fa fa-paper-plane" /> Send
              </button>
            </>
          )}

          <button className="btn-cancel" onClick={handleCancelClick}>
            <i className="fa fa-times" /> Cancel
          </button><br /><br />

        </div>

        <SuccessModal
          show={success}
          title="Success"
          message={successMessage}
          onClose={handleSuccessClose}
        />

        {errorMessage && (
          <SuccessModal title="Error" show={!!errorMessage} message={errorMessage} onClose={() => setErrorMessage("")} />
        )}

        {cancelSuccess && (
          <SuccessModal
            show={cancelSuccess}
            title="Success"
            message="Document cancelled successfully."
            onClose={handleCancelSuccessClose}
          />
        )}

        {showCancelConfirm && (
          <div className="confirm-overlay">
            <div className="confirm-box">
              <h4>Cancel Document?</h4>
              <p>Do you want to cancel this document?</p>

              <div className="confirm-actions">
                <button className="btn-send" onClick={handleConfirmCancel}>
                  Yes
                </button>

                <button className="btn-cancel" onClick={() => setShowCancelConfirm(false)}>
                  No
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      <Footer variant="dashboard" /><br /><br />

    </Dashboardsidebar>
  );
};

SendPage.propTypes = {
  auth: PropTypes.shape({
    createNewSaveAgreement: PropTypes.func.isRequired,
    cancelAgreementUploadData: PropTypes.func.isRequired,
    getUserInfoList: PropTypes.func.isRequired,
    sendAgreementAction: PropTypes.func.isRequired,
  }).isRequired,
  loader: PropTypes.shape({
    setLoading: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectModels(["auth", "loader"])(SendPage);